import { Overlay } from '@angular/cdk/overlay';
import { NgIf, AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, HostBinding, HostListener, inject, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY, MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations/public-api';
import { debounceTime, filter, map, Subject, takeUntil } from 'rxjs';
import { SearchService } from './search.service';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'search',
    templateUrl: './search.component.html',
    encapsulation: ViewEncapsulation.None,
    exportAs: 'fuseSearch',
    animations: fuseAnimations,
    standalone: true,
    imports: [NgIf, MatButtonModule, MatIconModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatTooltipModule, AsyncPipe],
    providers: [
        {
            provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
            useFactory: () => {
                const overlay = inject(Overlay);
                return () => overlay.scrollStrategies.block();
            },
        },
    ],
})
export class SearchComponent implements OnChanges, OnInit, OnDestroy {
    @Input() appearance: 'basic' | 'bar' = 'basic';
    @Input() debounce: number = 300;
    @Input() minLength: number = 2;
    @Output() search: EventEmitter<any> = new EventEmitter<any>();

    opened: boolean = false;
    resultSets: any[];
    searchControl: UntypedFormControl = new UntypedFormControl();
    private _matAutocomplete: MatAutocomplete;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    textoBusqueda = new Subject<string>();

    /**
     * Constructor
     */
    constructor(
        private _elementRef: ElementRef,
        private _httpClient: HttpClient,
        private _renderer2: Renderer2,
        private _searchService: SearchService,
        private router: Router
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any {
        return {
            'search-appearance-bar': this.appearance === 'bar',
            'search-appearance-basic': this.appearance === 'basic',
            'search-opened': this.opened,
        };
    }

    private _barSearchInputElement: ElementRef;

    /**
     * Setter for bar search input
     *
     * @param value
     */
    @ViewChild('barSearchInput')
    set barSearchInput(value: ElementRef) {
        this._barSearchInputElement = value;
        // If the value exists, it means that the search input
        // is now in the DOM, and we can focus on the input..
        if (value) {
            // Give Angular time to complete the change detection cycle
            setTimeout(() => {
                // Focus to the input element and select text
                value.nativeElement.focus();
                value.nativeElement.select?.();
            });
        }
    }

    /**
     * Setter for mat-autocomplete element reference
     *
     * @param value
     */
    @ViewChild('matAutocomplete')
    set matAutocomplete(value: MatAutocomplete) {
        this._matAutocomplete = value;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnChanges(changes: SimpleChanges): void {
        // Appearance
        this.textoBusqueda = new Subject<string>();
        if ('appearance' in changes) {
            // To prevent any issues, close the
            // search after changing the appearance
            this.close(true);
        }
    }

    /**
     * On init
     */
    ngOnInit(): void {
        this._searchService.setSearchInput(new FormControl());

        this._searchService.focusSearch$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                if (this.isSearchVisible()) {
                    if (!this.opened) {
                        this.open();
                    } else if (this._barSearchInputElement) {
                        this._barSearchInputElement.nativeElement.focus();
                        this._barSearchInputElement.nativeElement.select?.();
                    }
                }
            });

        // // Subscribe to the search field value changes
        // this.searchControl.valueChanges
        //     .pipe(
        //         debounceTime(this.debounce),
        //         takeUntil(this._unsubscribeAll),
        //         map((value) =>
        //         {
        //             // Set the resultSets to null if there is no value or
        //             // the length of the value is smaller than the minLength
        //             // so the autocomplete panel can be closed
        //             if ( !value || value.length < this.minLength )
        //             {
        //                 this.resultSets = null;
        //             }

        //             // Continue
        //             return value;
        //         }),
        //         // Filter out undefined/null/false statements and also
        //         // filter out the values that are smaller than minLength
        //         filter(value => value && value.length >= this.minLength),
        //     )
        //     .subscribe((value) =>
        //     {
        //         console.log('aiuda',value)
        // this._httpClient.post('api/common/search', {query: value})
        //     .subscribe((resultSets: any) =>
        //     {
        //         // Store the result sets
        //         this.resultSets = resultSets;

        //         // Execute the event
        //         this.search.next(resultSets);
        // });
        // });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On keydown of the search input
     *
     * @param event
     */
    onKeydown(event: KeyboardEvent): void {
        // Escape
        if ((event.code === 'Escape' || event.code === 'Enter') && this.appearance === 'bar') {
            // If the appearance is 'bar' and the mat-autocomplete is not open, close the search
            this.close(event.code === 'Escape');
        }
    }

    /**
   * Open the search
   * Used in 'bar'
   */
    open(): void {
        // Return if it's already opened
        if (this.opened) {
            return;
        }
        this.textoBusqueda = new Subject<string>();
        // Open the search
        this.opened = true;
    }

    /**
     * Esta función sirve para cerrar la barra de busqueda
     * @param clear 
     * @returns 
     */
    close(clear: boolean = false): void {
        // Return if it's already closed
        if (!this.opened) {
            return;
        }
        if (clear) {
            // Clear the search input
            this._searchService.getSearchInput().setValue('');
        }
        // Close the search
        this.opened = false;
    }

    /**
     * Listener para cerrar el buscador al hacer click fuera
     * @param event 
     */
    @HostListener('document:click', ['$event'])
    onClickOutside(event: MouseEvent): void {
        if (this.opened && !this._elementRef.nativeElement.contains(event.target)) {
            this.close(false);
        }
    }

    clear(): void {
        // Clear the search input and close
        this.close(true);
    }


    /**
     * Esta función sirve para la obtencion de la referencia del filtro y datos
     * @param event Evento que llamo al filtro
     * @param origin Elemento que desencadeno el la activacion del filtro
     */
    eventoFiltro(event: Event, origin: MatButton) {
        this._searchService.setFilterOrigin(origin)
        this._searchService.getFilterEvent().next(event)
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    /**
     * ¿CÓMO OCULTAR ESTOS ICONOS EN NUEVAS PANTALLAS?
     * Por favor lee el archivo README_VISIBILIDAD_ICONOS.md ubicado en este mismo
     * directorio para ver las instrucciones de cómo ocultar los iconos de búsqueda y filtros.
     */
    isFilterVisible(): boolean {
        const currentUrl = this.router.url.split('?')[0];

        const isCC = currentUrl.startsWith('/clientes/cc-clientes') ||
            currentUrl.startsWith('/proveedores/cc-proveedores');
        if (isCC) {
            return currentUrl.endsWith('/detalle');
        }

        // Ocultar en la pantalla de Generar Orden (ya que la ruta contiene IDs dinámicos antes)
        if (currentUrl.includes('/generar-orden/')) {
            return false;
        }

        // Ocultar en el módulo de Gestión de Tareas (grilla y calendario)
        if (currentUrl.startsWith('/gestion-tareas')) {
            return false;
        }

        // Se Define las rutas que se desea excluir
        const excludedRoutes = [
            // Administracion
            '/clientes/cuenta-corriente',
            '/proveedores/cuenta-corriente',
            // Compras
            '/facturas-de-compras/nueva-factura',
            '/facturas-de-compras/editar-factura',
            '/facturas-de-compras/visualizar',
            '/ordenes-de-pago/alta',
            '/ordenes-de-pago/edicion',
            '/ordenes-de-pago/visualizar',
            '/ordenes-de-compra/alta',
            '/ordenes-de-compra/edicion',
            '/ordenes-de-compra/visualizar',
            '/ordenes-de-compra/ajuste',
            // Ventas
            '/calculadora-de-cobros',
            '/cobranzas/alta-cobro',
            '/cobranzas/edicion-cobro',
            '/cobranzas/visualizar',
            '/facturas/nueva-factura',
            '/facturas/guardar-factura',
            '/facturas/editar-factura',
            '/facturas/visualizar',
            '/ventas/alta',
            '/ventas/edicion',
            '/ventas/visualizar',
            '/ventas/venta-detalle',
            // Seguridad
            '/empresas/nueva-empresa',
            '/empresas/editar-empresa',
            '/empresas/consultar-empresa',
            '/empresas/gestion-emails',
            '/items',
            '/roles',
            '/menues',
            // Stock
            '/fabricados/nuevo-producto',
            '/fabricados/editar-producto',
            '/fabricados/consulta-producto',
            '/facturas/enviar-factura-mail',
            '/entregas/nueva',
            '/entregas/editar',
            '/entregas/ver',
            '/remitos/alta',
            '/remitos/edicion',
            '/remitos/visualizar',
            '/entregas-externas',
            '/devoluciones-externas',
            '/movimientos-internos/alta',
            '/movimientos-internos/edicion',
            '/movimientos-internos/visualizar',
            '/stock-inicial/alta',
            '/stock-inicial/edicion',
            '/stock-inicial/visualizar',
            '/inventario/alta',
            '/inventario/visualizar',
            //RRHH
            '/rrhh/ofertas-laborales',
            '/rrhh/postulaciones',
            // Punto de Venta
            '/punto-de-venta/carga-rapida',
            // Soporte
            '/soporte/listado',
            '/devoluciones',
            // Prototipos / Indicadores
            '/indicadores-centro-costo',
            '/indicadores-de-venta',
            // Indicadores
            '/indicador-stock',
            // Prototipos
            '/prototipos/ventas-obras/nueva-venta',
            '/prototipos/anticipos-clientes',
            '/cobranzas-anticipos',
            '/prototipos/anticipos-proveedores',
            // Pañol / Entregas Rápidas
            '/entregas-rapidas',
            // Medios de Pago
            '/medios-de-pago'
        ];

        return !excludedRoutes.some(route => currentUrl.startsWith(route));
    }

    isSearchVisible(): boolean {
        const currentUrl = this.router.url.split('?')[0];

        // Si el buscador está deshabilitado o estamos en el detalle histórico (ruta termina en /detalle), se oculta
        if (this._searchService.getSearchInput()?.disabled || currentUrl.endsWith('/detalle')) {
            return false;
        }

        // Ocultar en la pantalla de Generar Orden (ya que la ruta contiene IDs dinámicos antes)
        if (currentUrl.includes('/generar-orden/')) {
            return false;
        }

        // Ocultar en el módulo de Gestión de Tareas (grilla y calendario)
        if (currentUrl.startsWith('/gestion-tareas')) {
            return false;
        }

        // Se Define las rutas que se desea excluir
        const excludedRoutes = [
            // Administracion
            '/clientes/cuenta-corriente',
            '/proveedores/cuenta-corriente',
            // Compras
            '/facturas-de-compras/nueva-factura',
            '/facturas-de-compras/editar-factura',
            '/facturas-de-compras/visualizar',
            '/ordenes-de-pago/alta',
            '/ordenes-de-pago/edicion',
            '/ordenes-de-pago/visualizar',
            '/ordenes-de-compra/alta',
            '/ordenes-de-compra/edicion',
            '/ordenes-de-compra/visualizar',
            '/ordenes-de-compra/ajuste',
            '/periodos-de-compras',
            // Ventas
            '/calculadora-de-cobros',
            '/cobranzas/alta-cobro',
            '/cobranzas/edicion-cobro',
            '/cobranzas/visualizar',
            '/facturas/nueva-factura',
            '/facturas/guardar-factura',
            '/facturas/editar-factura',
            '/facturas/visualizar',
            '/ventas/alta',
            '/ventas/edicion',
            '/ventas/visualizar',
            '/ventas/venta-detalle',
            // Seguridad
            '/empresas/nueva-empresa',
            '/empresas/editar-empresa',
            '/empresas/consultar-empresa',
            // Stock
            '/fabricados/nuevo-producto',
            '/fabricados/editar-producto',
            '/fabricados/consulta-producto',
            '/facturas/enviar-factura-mail',
            '/entregas/nueva',
            '/entregas/editar',
            '/entregas/ver',
            '/remitos/alta',
            '/remitos/edicion',
            '/remitos/visualizar',
            '/entregas-externas',
            '/devoluciones-externas',
            '/movimientos-internos/alta',
            '/movimientos-internos/edicion',
            '/movimientos-internos/visualizar',
            '/stock-inicial/alta',
            '/stock-inicial/edicion',
            '/stock-inicial/visualizar',
            '/inventario/alta',
            '/inventario/visualizar',
            //RRHH
            '/rrhh/ofertas-laborales',
            '/rrhh/postulaciones',
            // Punto de Venta
            '/punto-de-venta/carga-rapida',
            // Soporte
            '/soporte/listado',
            // Prototipos / Indicadores
            '/indicadores-centro-costo',
            // Indicadores
            '/indicador-stock',
            // Prototipos
            '/indicadores-de-venta',
            '/prototipos/ventas-obras/nueva-venta',
            '/prototipos/anticipos-clientes',
            '/cobranzas-anticipos',
            '/prototipos/anticipos-proveedores',
            // Pañol / Entregas Rápidas
            '/entregas-rapidas',
            // Medios de Pago
            '/medios-de-pago'
        ];

        return !excludedRoutes.some(route => currentUrl.startsWith(route));
    }
}
